import { useState } from "react";
import { useForm } from "react-hook-form";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/contexts/ProfileContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Pencil, Save, X, Sparkles, Loader2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import { CompanyLogo } from "../CompanyLogo";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface MyCompanyTabProps {
  initialData: any;
}

export const MyCompanyTab = ({ initialData }: MyCompanyTabProps) => {
  const { user } = useAuth();
  const { refetchProfile } = useProfile();
  const { toast } = useToast();
  const [companyLogoUrl, setCompanyLogoUrl] = useState(initialData?.company_logo_url);
  const [aiWebsiteUrl, setAiWebsiteUrl] = useState("");
  const [isAiScanning, setIsAiScanning] = useState(false);

  const form = useForm({
    defaultValues: {
      organization: initialData?.organization || "",
      company_location: initialData?.company_location || "",
      company_size: initialData?.company_size || "",
      company_industry: initialData?.company_industry || "",
      company_description: initialData?.company_description || "",
      company_website: initialData?.company_website || "",
      company_linkedin: initialData?.company_linkedin || "",
      company_twitter: initialData?.company_twitter || "",
      company_facebook: initialData?.company_facebook || "",
      company_instagram: initialData?.company_instagram || "",
    },
  });

  const onSubmit = async (data: any) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          ...data,
          company_logo_url: companyLogoUrl
        })
        .eq('id', user?.id);

      if (error) throw error;

      // Force refresh the profile context to get the updated data with new fields
      await refetchProfile();

      toast({
        title: "Company information updated successfully",
      });
      
      // Reset form dirty state with updated data
      form.reset({
        ...data,
        company_logo_url: companyLogoUrl
      });
    } catch (error) {
      toast({
        title: "Error updating company information",
        variant: "destructive",
      });
    }
  };

  const handleAiFill = async () => {
    if (!aiWebsiteUrl.trim()) {
      toast({
        title: "Please enter a website URL",
        variant: "destructive",
      });
      return;
    }

    setIsAiScanning(true);
    try {
      console.log('Scanning website:', aiWebsiteUrl);
      
      const { data, error } = await supabase.functions.invoke('scan-company-website', {
        body: { websiteUrl: aiWebsiteUrl }
      });

      if (error) {
        console.error('Supabase function error:', error);
        throw new Error(error.message || 'Failed to scan website');
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to extract company information');
      }

      const extractedData = data.data;
      console.log('Extracted data:', extractedData);

      // Update form with extracted data
      if (extractedData.name) {
        form.setValue('organization', extractedData.name, { shouldDirty: true });
      }
      if (extractedData.industry) {
        form.setValue('company_industry', extractedData.industry, { shouldDirty: true });
      }
      if (extractedData.description) {
        form.setValue('company_description', extractedData.description, { shouldDirty: true });
      }
      if (extractedData.size) {
        form.setValue('company_size', extractedData.size, { shouldDirty: true });
      }
      if (!form.watch('company_website')) {
        form.setValue('company_website', aiWebsiteUrl, { shouldDirty: true });
      }

      // Update logo if found
      if (extractedData.logo) {
        setCompanyLogoUrl(extractedData.logo);
      }

      // Update social links if found
      if (extractedData.linkedin) {
        form.setValue('company_linkedin', extractedData.linkedin, { shouldDirty: true });
      }
      if (extractedData.twitter) {
        form.setValue('company_twitter', extractedData.twitter, { shouldDirty: true });
      }
      if (extractedData.facebook) {
        form.setValue('company_facebook', extractedData.facebook, { shouldDirty: true });
      }
      if (extractedData.instagram) {
        form.setValue('company_instagram', extractedData.instagram, { shouldDirty: true });
      }

      toast({
        title: "✨ AI scan completed!",
        description: "Company information has been filled automatically. Review and save the changes.",
      });

    } catch (error: any) {
      console.error('Error scanning website:', error);
      toast({
        title: "Something went wrong",
        description: "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsAiScanning(false);
    }
  };

  return (
    <div className="p-6">
      {/* AI Fill Section */}
      <Card className="mb-6 border-2 border-primary/20 bg-gradient-to-r from-primary/5 to-secondary/5">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="h-5 w-5 text-primary" />
            AI Company Fill
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Enter your company website URL and let AI automatically fill in your company details
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="https://yourcompany.com"
              value={aiWebsiteUrl}
              onChange={(e) => setAiWebsiteUrl(e.target.value)}
              className="flex-1"
              type="url"
            />
            <Button
              type="button"
              onClick={handleAiFill}
              disabled={isAiScanning || !aiWebsiteUrl.trim()}
              className="px-6"
            >
              {isAiScanning ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Scanning...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  AI Fill
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <h3 className="text-lg font-medium text-foreground mb-4">Company logo</h3>
            <CompanyLogo
              logoUrl={companyLogoUrl}
              setLogoUrl={setCompanyLogoUrl}
              companyName={initialData?.organization || "Company"}
            />
          </div>

          <div className="space-y-5">
            <FormField
              control={form.control}
              name="organization"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground font-medium">Company Name</FormLabel>
                  <FormControl>
                    <Input 
                      {...field} 
                      placeholder="Enter company name" 
                      className="mt-1"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="company_location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground font-medium">Location</FormLabel>
                  <FormControl>
                    <Input 
                      {...field} 
                      placeholder="City, Country" 
                      className="mt-1"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="company_size"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground font-medium">Company Size</FormLabel>
                  <FormControl>
                    <Input 
                      {...field} 
                      placeholder="e.g. 50-100 employees" 
                      className="mt-1"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="company_industry"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground font-medium">Industry</FormLabel>
                  <FormControl>
                    <Input 
                      {...field} 
                      placeholder="e.g. Technology, Healthcare" 
                      className="mt-1"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="company_description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground font-medium">About Us</FormLabel>
                  <FormControl>
                    <Textarea 
                      {...field} 
                      placeholder="Tell us about your company" 
                      className="min-h-[120px] mt-1"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="company_website"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground font-medium">Website</FormLabel>
                  <FormControl>
                    <Input 
                      {...field} 
                      placeholder="https://yourcompany.com" 
                      className="mt-1"
                      type="url"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="company_linkedin"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground font-medium">LinkedIn</FormLabel>
                  <FormControl>
                    <Input 
                      {...field} 
                      placeholder="https://linkedin.com/company/yourcompany" 
                      className="mt-1"
                      type="url"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="company_twitter"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground font-medium">Twitter</FormLabel>
                  <FormControl>
                    <Input 
                      {...field} 
                      placeholder="https://twitter.com/yourcompany" 
                      className="mt-1"
                      type="url"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="company_facebook"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground font-medium">Facebook</FormLabel>
                  <FormControl>
                    <Input 
                      {...field} 
                      placeholder="https://facebook.com/yourcompany" 
                      className="mt-1"
                      type="url"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="company_instagram"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground font-medium">Instagram</FormLabel>
                  <FormControl>
                    <Input 
                      {...field} 
                      placeholder="https://instagram.com/yourcompany" 
                      className="mt-1"
                      type="url"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex justify-end pt-4">
            <Button 
              type="submit" 
              className="px-6 py-2 rounded-md"
              disabled={form.formState.isSubmitting || !form.formState.isDirty}
            >
              Save changes
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};